import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useEngagementEditor } from './EngagementEditorContext';
import { AdviseLineItem } from '../../../../types/advise';
import { formatCurrency } from '../../../../utils/helpers';
import { categoryTotal, engagementPriceCategories, supplierTotal } from '../pricing';

export const PricingPanel = () => {
  const { engagement, suppliers, canEdit, editEngagement } = useEngagementEditor();
  const [hideUnused, setHideUnused] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [localTelephony, setLocalTelephony] = useState<Record<string, { quantity: string; rate: string }>>({});

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement.selected_suppliers?.includes(v.id)),
    [suppliers, engagement.selected_suppliers]
  );

  const quotes = useMemo(() => engagement.uploaded_quotes || [], [engagement.uploaded_quotes]);

  const mapping = engagement.line_item_mapping || {};
  const telephonyOverrides = engagement.telephony_usage_overrides || {};
  const customCategories = engagement.custom_price_categories || [];

  const allCategories = useMemo(() => {
    const mappedCategories = new Set<string>();
    Object.values(mapping).forEach((supplierMap) => {
      Object.values(supplierMap).forEach((cat) => {
        if (cat) mappedCategories.add(cat);
      });
    });
    return Array.from(
      new Set([...engagementPriceCategories({ custom_price_categories: customCategories }), ...mappedCategories])
    );
  }, [customCategories, mapping]);

  // Build line items per supplier from quotes
  const supplierLineItems = useMemo(() => {
    const result: Record<string, AdviseLineItem[]> = {};
    selectedSuppliers.forEach((v) => {
      const supplierQuotes = quotes.filter((q) => q.supplier_name?.toLowerCase() === v.supplier_name.toLowerCase());
      const items: AdviseLineItem[] = [];
      supplierQuotes.forEach((q) => {
        if (q.extracted_pricing?.line_items) {
          items.push(...q.extracted_pricing.line_items);
        }
      });
      result[v.id] = items;
    });
    return result;
  }, [selectedSuppliers, quotes]);

  const getCategoryTotal = useCallback(
    (supplierId: string, category: string, billing: 'monthly' | 'one-time'): number =>
      categoryTotal(
        supplierLineItems[supplierId] || [],
        mapping[supplierId] || {},
        telephonyOverrides[supplierId],
        category,
        billing
      ),
    [supplierLineItems, mapping, telephonyOverrides]
  );

  const getSupplierTotal = useCallback(
    (supplierId: string, billing: 'monthly' | 'one-time') =>
      supplierTotal(
        supplierLineItems[supplierId] || [],
        mapping[supplierId] || {},
        telephonyOverrides[supplierId],
        allCategories,
        billing
      ),
    [supplierLineItems, mapping, telephonyOverrides, allCategories]
  );

  // Init local telephony when the engagement or supplier set changes — not on every
  // telephonyOverrides mutation, which would clobber an in-progress qty/rate edit.
  useEffect(() => {
    const init: Record<string, { quantity: string; rate: string }> = {};
    selectedSuppliers.forEach((v) => {
      const o = telephonyOverrides[v.id];
      init[v.id] = {
        quantity: o?.quantity?.toString() || '',
        rate: o?.rate?.toString() || ''
      };
    });
    setLocalTelephony(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagement.id, selectedSuppliers]);

  const handleMappingChange = useCallback(
    (supplierId: string, itemName: string, category: string) => {
      const updated = {
        ...mapping,
        [supplierId]: {
          ...(mapping[supplierId] || {}),
          [itemName]: category
        }
      };
      editEngagement({ line_item_mapping: updated });
    },
    [mapping, editEngagement]
  );

  // TODO: clamp telephony qty/rate to non-negative before persisting — negatives feed nonsensical totals.
  const handleTelephonyBlur = useCallback(
    (supplierId: string) => {
      const local = localTelephony[supplierId];
      if (!local) return;
      const qty = parseFloat(local.quantity) || 0;
      const rate = parseFloat(local.rate) || 0;
      const updated = {
        ...telephonyOverrides,
        [supplierId]: { quantity: qty, rate }
      };
      editEngagement({ telephony_usage_overrides: updated });
    },
    [localTelephony, telephonyOverrides, editEngagement]
  );

  const handleAddCategory = useCallback(() => {
    const trimmed = newCategory.trim();
    if (!trimmed || allCategories.includes(trimmed)) return;
    editEngagement({ custom_price_categories: [...customCategories, trimmed] });
    setNewCategory('');
  }, [newCategory, allCategories, customCategories, editEngagement]);

  const visibleCategories = hideUnused
    ? allCategories.filter((cat) =>
        selectedSuppliers.some(
          (v) => getCategoryTotal(v.id, cat, 'monthly') > 0 || getCategoryTotal(v.id, cat, 'one-time') > 0
        )
      )
    : allCategories;

  if (selectedSuppliers.length === 0) {
    return <Alert severity="info">No suppliers selected for pricing comparison.</Alert>;
  }

  return (
    <Box className="flex flex-col gap-4">
      {/* Controls */}
      <Box className="flex items-center justify-between">
        <FormControlLabel
          control={
            <Switch
              checked={hideUnused}
              onChange={(e) => setHideUnused(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2">Hide unused categories</Typography>}
        />
        {canEdit && (
          <Box className="flex gap-2">
            <TextField
              size="small"
              placeholder="New category..."
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCategory();
                }
              }}
              sx={{ width: 200 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              disabled={!newCategory.trim()}>
              Add
            </Button>
          </Box>
        )}
      </Box>

      {/* Price Comparison Table */}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2">Category</Typography>
                </TableCell>
                {selectedSuppliers.map((v) => (
                  <TableCell
                    key={v.id}
                    align="right"
                    colSpan={2}>
                    <Typography variant="subtitle2">{v.supplier_name}</Typography>
                    <Box className="flex justify-end gap-4">
                      <Typography
                        variant="caption"
                        color="textSecondary">
                        Monthly
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary">
                        One-Time
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCategories.map((cat) => (
                <TableRow
                  key={cat}
                  hover>
                  <TableCell>
                    <Typography variant="body2">{cat}</Typography>
                  </TableCell>
                  {selectedSuppliers.map((v) => (
                    <TableCell
                      key={v.id}
                      align="right"
                      colSpan={2}>
                      <Box className={'flex justify-end gap-4'}>
                        <Typography variant="body2">
                          {formatCurrency(getCategoryTotal(v.id, cat, 'monthly'), { zeroDisplay: '—' })}
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(getCategoryTotal(v.id, cat, 'one-time'), { zeroDisplay: '—' })}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Telephony Usage Override Row */}
              {canEdit && (
                <TableRow>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="textSecondary">
                      Telephony Usage (override)
                    </Typography>
                  </TableCell>
                  {selectedSuppliers.map((v) => (
                    <TableCell
                      key={v.id}
                      align="right"
                      colSpan={2}>
                      <Box className={'flex items-center ' + 'justify-end gap-1'}>
                        <TextField
                          size="small"
                          placeholder="Qty"
                          value={localTelephony[v.id]?.quantity || ''}
                          onChange={(e) =>
                            setLocalTelephony((prev) => ({
                              ...prev,
                              [v.id]: {
                                ...prev[v.id],
                                quantity: e.target.value
                              }
                            }))
                          }
                          onBlur={() => handleTelephonyBlur(v.id)}
                          sx={{ width: 70 }}
                          type="number"
                        />
                        <Typography variant="caption">@</Typography>
                        <TextField
                          size="small"
                          placeholder="Rate"
                          value={localTelephony[v.id]?.rate || ''}
                          onChange={(e) =>
                            setLocalTelephony((prev) => ({
                              ...prev,
                              [v.id]: {
                                ...prev[v.id],
                                rate: e.target.value
                              }
                            }))
                          }
                          onBlur={() => handleTelephonyBlur(v.id)}
                          sx={{ width: 70 }}
                          type="number"
                        />
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              )}

              {/* Totals */}
              <TableRow
                sx={{
                  '& td': { fontWeight: 'bold' }
                }}>
                <TableCell>
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold">
                    Total
                  </Typography>
                </TableCell>
                {selectedSuppliers.map((v) => (
                  <TableCell
                    key={v.id}
                    align="right"
                    colSpan={2}>
                    <Box className="flex justify-end gap-4">
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold">
                        {formatCurrency(getSupplierTotal(v.id, 'monthly'), { zeroDisplay: '—' })}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold">
                        {formatCurrency(getSupplierTotal(v.id, 'one-time'), { zeroDisplay: '—' })}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Line Item Categorization */}
      {canEdit &&
        selectedSuppliers.map((supplier) => {
          const items = supplierLineItems[supplier.id] || [];
          if (items.length === 0) return null;

          const supplierMap = mapping[supplier.id] || {};

          return (
            <Card
              key={supplier.id}
              variant="outlined">
              <CardContent>
                <Typography
                  variant="subtitle2"
                  gutterBottom>
                  {supplier.supplier_name} — Line Item Categorization
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Line Item</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell>Billing</TableCell>
                      <TableCell>Category</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2">{item.item_name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.total_price, { zeroDisplay: '—' })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.billing_cycle}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={supplierMap[item.item_name] || ''}
                            onChange={(e) => handleMappingChange(supplier.id, item.item_name, e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 180 }}>
                            <MenuItem value="">
                              <em>Uncategorized</em>
                            </MenuItem>
                            {allCategories.map((cat) => (
                              <MenuItem
                                key={cat}
                                value={cat}>
                                {cat}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}
    </Box>
  );
};
